import { supabase } from "@/integrations/supabase/client";
import { captureAttribution, getAttribution } from "./attribution";
import { isExcludedPath } from "@/lib/analytics-loader";

/** True when the current location is an internal / admin / auth route that
 *  must never be ingested as public marketing traffic. */
function isExcludedNow(): boolean {
  if (typeof window === "undefined") return true;
  return isExcludedPath(window.location.pathname);
}

const SESSION_KEY = "cws_session_id";
const SESSION_TS_KEY = "cws_session_ts";
const VISITOR_KEY = "cws_visitor_id";
const VISITOR_TS_KEY = "cws_visitor_ts";
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 min inactivity rotates session (industry standard)
const VISITOR_TTL_MS = 2 * 365 * 24 * 60 * 60 * 1000; // 2-year stable browser id
const HEARTBEAT_MS = 15_000;
const FLUSH_MS = 2_000;
const BATCH_MAX = 10;

// Surface tracker errors so the diagnostics tab + DevTools can see them.
declare global {
  interface Window {
    __cwsTracker?: {
      errors: { at: string; where: string; message: string }[];
      lastUpsertAt?: string;
      lastUpsertOk?: boolean;
      lastEventsFlushedAt?: string;
      visitorId?: string;
      sessionId?: string;
      initializedAt?: string;
    };
  }
}
function trackerState() {
  if (typeof window === "undefined") return null;
  if (!window.__cwsTracker) window.__cwsTracker = { errors: [] };
  return window.__cwsTracker;
}
function recordError(where: string, err: unknown) {
  const state = trackerState();
  if (!state) return;
  const message = err instanceof Error ? err.message : String(err);
  state.errors.push({ at: new Date().toISOString(), where, message });
  if (state.errors.length > 50) state.errors.splice(0, state.errors.length - 50);
  // eslint-disable-next-line no-console
  console.warn(`[cws-tracker:${where}]`, message);
}

type EventType =
  | "pageview"
  | "cta_click"
  | "form_submit"
  | "form_start"
  | "scroll_depth"
  | "heartbeat"
  | "custom";

interface QueuedEvent {
  event_type: EventType;
  path?: string;
  title?: string;
  target?: string;
  meta?: Record<string, unknown>;
  created_at: string;
}

const BOT_RE = /bot|crawler|spider|crawling|slurp|facebookexternalhit|whatsapp|telegram|preview/i;

function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function detectDevice(): { device: string; browser: string; os: string } {
  const ua = navigator.userAgent || "";
  const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  const isTablet = /iPad|Tablet/i.test(ua);
  const device = isTablet ? "tablet" : isMobile ? "mobile" : "desktop";
  let browser = "Other";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Safari\//.test(ua)) browser = "Safari";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  let os = "Other";
  if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac OS/.test(ua)) os = "macOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad|iOS/.test(ua)) os = "iOS";
  else if (/Linux/.test(ua)) os = "Linux";
  return { device, browser, os };
}

function getUtmParams() {
  const sp = new URLSearchParams(window.location.search);
  return {
    utm_source: sp.get("utm_source") || undefined,
    utm_medium: sp.get("utm_medium") || undefined,
    utm_campaign: sp.get("utm_campaign") || undefined,
    utm_term: sp.get("utm_term") || undefined,
    utm_content: sp.get("utm_content") || undefined,
  };
}

class VisitorTracker {
  private sessionId = "";
  private visitorId = "";
  private queue: QueuedEvent[] = [];
  private flushTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private initialized = false;
  private scrollMilestones = new Set<number>();
  private lastPath = "";
  private isBot = false;

  init() {
    if (this.initialized || typeof window === "undefined") return;
    // Never ingest from admin / auth / customer-portal routes.
    if (isExcludedNow()) return;
    this.initialized = true;

    // Bot filter
    if ((navigator as any).webdriver || BOT_RE.test(navigator.userAgent || "")) {
      this.isBot = true;
    }

    // Stable visitor id — 2-year browser cookie. One per device/browser.
    try {
      const storedV = localStorage.getItem(VISITOR_KEY);
      const tsV = parseInt(localStorage.getItem(VISITOR_TS_KEY) || "0", 10);
      if (storedV && Date.now() - tsV < VISITOR_TTL_MS) {
        this.visitorId = storedV;
      } else {
        this.visitorId = "v_" + uuid();
        localStorage.setItem(VISITOR_KEY, this.visitorId);
      }
      localStorage.setItem(VISITOR_TS_KEY, String(Date.now()));
    } catch (err) {
      recordError("visitor-id", err);
      this.visitorId = "v_" + uuid();
    }

    // Session id — rotates after 30 min of inactivity.
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      const ts = parseInt(localStorage.getItem(SESSION_TS_KEY) || "0", 10);
      if (stored && Date.now() - ts < SESSION_IDLE_MS) {
        this.sessionId = stored;
      } else {
        this.sessionId = uuid();
        localStorage.setItem(SESSION_KEY, this.sessionId);
      }
      localStorage.setItem(SESSION_TS_KEY, String(Date.now()));
    } catch (err) {
      recordError("session-id", err);
      this.sessionId = uuid();
    }

    const state = trackerState();
    if (state) {
      state.visitorId = this.visitorId;
      state.sessionId = this.sessionId;
      state.initializedAt = new Date().toISOString();
    }

    void this.upsertSession(true);
    this.trackPageview();
    this.attachListeners();
    this.startHeartbeat();

    // Sync user_id on auth changes
    supabase.auth.getSession().then(({ data }) => {
      const uid = data.session?.user?.id;
      if (uid) void this.updateUserId(uid);
    });
    supabase.auth.onAuthStateChange((_e, session) => {
      const uid = session?.user?.id;
      if (uid) void this.updateUserId(uid);
    });
  }

  private async upsertSession(isFirst: boolean) {
    if (this.isBot) return;
    const { device, browser, os } = detectDevice();
    const path = window.location.pathname + window.location.search;
    const { first, last, hasUtm } = captureAttribution();
    const payload: any = {
      session_id: this.sessionId,
      visitor_id: this.visitorId,
      last_seen_at: new Date().toISOString(),
      current_path: window.location.pathname,
      device,
      browser,
      os,
    };
    if (isFirst) {
      payload.first_seen_at = new Date().toISOString();
      payload.landing_path = path;
      payload.referrer = document.referrer || null;
      const utm = getUtmParams();
      Object.assign(payload, utm);
      if (first) {
        payload.utm_source = payload.utm_source || first.source;
        payload.utm_medium = payload.utm_medium || first.medium;
        payload.utm_campaign = payload.utm_campaign || first.campaign;
        payload.utm_term = payload.utm_term || first.term;
        payload.utm_content = payload.utm_content || first.content;
      }
    }
    if (hasUtm && last) {
      payload.last_utm_source = last.source;
      payload.last_utm_medium = last.medium;
      payload.last_utm_campaign = last.campaign;
      payload.last_utm_term = last.term;
      payload.last_utm_content = last.content;
      payload.last_seen_landing_path = path;
    }
    const state = trackerState();
    try {
      const { session_id: _sid, visitor_id: _vid, ...rest } = payload;
      const { error } = await supabase.rpc("track_visitor_session_upsert", {
        p_session_id: this.sessionId,
        p_visitor_id: this.visitorId,
        p_payload: rest as any,
      });
      if (error) {
        recordError("upsert-session", error);
        if (state) { state.lastUpsertAt = new Date().toISOString(); state.lastUpsertOk = false; }
      } else if (state) {
        state.lastUpsertAt = new Date().toISOString();
        state.lastUpsertOk = true;
      }
    } catch (err) {
      recordError("upsert-session", err);
      if (state) { state.lastUpsertAt = new Date().toISOString(); state.lastUpsertOk = false; }
    }
  }

  private async updateUserId(userId: string) {
    if (this.isBot) return;
    try {
      const { error } = await supabase
        .from("visitor_sessions")
        .update({ user_id: userId })
        .eq("session_id", this.sessionId);
      if (error) recordError("update-user-id", error);
    } catch (err) {
      recordError("update-user-id", err);
    }
  }

  /** Rotate session id if more than SESSION_IDLE_MS has elapsed since the last activity. */
  private maybeRotateSession() {
    try {
      const ts = parseInt(localStorage.getItem(SESSION_TS_KEY) || "0", 10);
      if (ts && Date.now() - ts > SESSION_IDLE_MS) {
        this.sessionId = uuid();
        localStorage.setItem(SESSION_KEY, this.sessionId);
        const state = trackerState();
        if (state) state.sessionId = this.sessionId;
        void this.upsertSession(true);
      }
      localStorage.setItem(SESSION_TS_KEY, String(Date.now()));
    } catch (err) {
      recordError("rotate-session", err);
    }
  }

  private enqueue(ev: Omit<QueuedEvent, "created_at">) {
    if (this.isBot) return;
    if (isExcludedNow()) return;
    this.maybeRotateSession();
    this.queue.push({ ...ev, created_at: new Date().toISOString() });
    if (this.queue.length >= BATCH_MAX) {
      void this.flush();
    } else if (this.flushTimer === null) {
      this.flushTimer = window.setTimeout(() => void this.flush(), FLUSH_MS);
    }
  }

  private async flush() {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (!this.queue.length) return;
    const batch = this.queue.splice(0, this.queue.length).map((e) => ({
      ...e,
      meta: (e.meta ?? {}) as any,
      session_id: this.sessionId,
    }));
    const state = trackerState();
    try {
      const { error: evErr } = await supabase.from("visitor_events").insert(batch as any);
      if (evErr) recordError("insert-events", evErr);
      const { error: upErr } = await supabase.rpc("track_visitor_session_upsert", {
        p_session_id: this.sessionId,
        p_visitor_id: this.visitorId,
        p_payload: {
          last_seen_at: new Date().toISOString(),
          current_path: window.location.pathname,
        } as any,
      });
      if (upErr) recordError("touch-session", upErr);
      if (state) state.lastEventsFlushedAt = new Date().toISOString();
    } catch (err) {
      recordError("flush", err);
    }
  }

  trackPageview() {
    if (isExcludedNow()) return;
    const path = window.location.pathname + window.location.search;
    if (path === this.lastPath) return;
    this.lastPath = path;
    this.scrollMilestones.clear();
    // If this navigation introduced UTMs, refresh session attribution.
    const sp = new URLSearchParams(window.location.search);
    if (sp.has("utm_source") || sp.has("utm_medium") || sp.has("utm_campaign")) {
      void this.upsertSession(false);
    }
    this.enqueue({
      event_type: "pageview",
      path: window.location.pathname,
      title: document.title,
    });
  }

  trackCustom(target: string, meta?: Record<string, unknown>) {
    this.enqueue({
      event_type: "custom",
      path: window.location.pathname,
      target,
      meta,
    });
  }

  private attachListeners() {
    // CTA clicks
    document.addEventListener(
      "click",
      (e) => {
        const el = (e.target as HTMLElement | null)?.closest("[data-cta], a[href^='tel:'], a[href^='mailto:']") as HTMLElement | null;
        if (!el) return;
        const target =
          el.getAttribute("data-cta") ||
          (el as HTMLAnchorElement).href ||
          el.textContent?.trim().slice(0, 60) ||
          "";
        this.enqueue({
          event_type: "cta_click",
          path: window.location.pathname,
          target,
          meta: { attribution: getAttribution() },
        });
      },
      { capture: true, passive: true }
    );

    // Form submit + form start
    document.addEventListener(
      "submit",
      (e) => {
        const form = e.target as HTMLFormElement | null;
        if (!form) return;
        const id = form.getAttribute("data-form-id") || form.id || form.getAttribute("name") || "form";
        this.enqueue({
          event_type: "form_submit",
          path: window.location.pathname,
          target: id,
          meta: { attribution: getAttribution() },
        });
      },
      { capture: true, passive: true }
    );

    const startedForms = new WeakSet<HTMLFormElement>();
    document.addEventListener(
      "focusin",
      (e) => {
        const input = e.target as HTMLElement | null;
        const form = input?.closest("form") as HTMLFormElement | null;
        if (!form || startedForms.has(form)) return;
        startedForms.add(form);
        const id = form.getAttribute("data-form-id") || form.id || form.getAttribute("name") || "form";
        this.enqueue({
          event_type: "form_start",
          path: window.location.pathname,
          target: id,
        });
      },
      { capture: true, passive: true }
    );

    // Scroll depth
    const onScroll = () => {
      const h = document.documentElement;
      const pct = Math.round(((window.scrollY + window.innerHeight) / h.scrollHeight) * 100);
      [25, 50, 75, 100].forEach((m) => {
        if (pct >= m && !this.scrollMilestones.has(m)) {
          this.scrollMilestones.add(m);
          this.enqueue({
            event_type: "scroll_depth",
            path: window.location.pathname,
            target: String(m),
          });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Flush on hide
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void this.flush();
    });
    window.addEventListener("pagehide", () => void this.flush());
  }

  private startHeartbeat() {
    this.heartbeatTimer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void this.upsertSession(false);
    }, HEARTBEAT_MS);
  }

  getSessionId() {
    return this.sessionId;
  }
  getVisitorId() {
    return this.visitorId;
  }
}

export const visitorTracker = new VisitorTracker();
